import React from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

/**
 * Full-bleed gradient CTA card with a faint grid pattern.
 * Designed to match the v0 preview screenshot.
 */
export default function FinalCta({ onStart, onDemo }) {
  return (
    <section className="px-4 pb-16 pt-4">
      <div className="mx-auto max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
          className="relative overflow-hidden rounded-[2.5rem] px-6 py-16 text-center shadow-2xl sm:px-12 md:px-16 md:py-24"
          style={{
            background:
              "radial-gradient(at 20% 20%, rgba(167,139,250,0.5) 0%, transparent 55%), radial-gradient(at 80% 70%, rgba(56,189,248,0.4) 0%, transparent 55%), radial-gradient(at 60% 100%, rgba(244,114,182,0.35) 0%, transparent 60%), #0A0A14",
            boxShadow:
              "0 40px 100px -20px rgba(167,139,250,0.4), inset 0 0 0 1px rgba(255,255,255,0.06)",
          }}
        >
          {/* grid overlay */}
          <div
            aria-hidden="true"
            className="cta-grid absolute inset-0 opacity-30"
          />
          {/* soft corner glows */}
          <div
            aria-hidden="true"
            className="absolute -right-40 -top-40 size-96 rounded-full bg-white/25 blur-3xl"
          />
          <div
            aria-hidden="true"
            className="absolute -bottom-32 -left-32 size-80 rounded-full bg-cyan-300/30 blur-3xl"
          />

          <div className="relative">
            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{
                duration: 0.7,
                delay: 0.1,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="font-heading text-balance text-4xl font-bold leading-[1.05] text-white sm:text-5xl md:text-6xl lg:text-7xl"
            >
              Ready to fill
              <br />
              your calendar?
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{
                duration: 0.6,
                delay: 0.25,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="mx-auto mt-7 max-w-xl text-pretty text-base leading-relaxed text-white/90 sm:text-lg"
            >
              Join the growing number of beauty professionals exploring
              SlotBook. Start your free trial today.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{
                duration: 0.6,
                delay: 0.4,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="mt-9 flex flex-wrap items-center justify-center gap-3"
            >
              <motion.button
                type="button"
                onClick={onStart}
                whileHover={{ scale: 1.04, y: -2 }}
                whileTap={{ scale: 0.97 }}
                transition={{ type: "spring", stiffness: 360, damping: 22 }}
                className="group inline-flex h-12 items-center gap-2 rounded-full bg-white px-7 text-base font-semibold text-slate-900 shadow-lg shadow-violet-900/40"
              >
                Start Free Trial
                <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
              </motion.button>
              <motion.button
                type="button"
                onClick={onDemo}
                whileHover={{
                  scale: 1.03,
                  backgroundColor: "rgba(255,255,255,0.22)",
                }}
                whileTap={{ scale: 0.97 }}
                transition={{ type: "spring", stiffness: 360, damping: 22 }}
                className="inline-flex h-12 items-center rounded-full border border-white/40 bg-white/10 px-7 text-base font-semibold text-white backdrop-blur"
              >
                Book Demo
              </motion.button>
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 0.8 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 0.6, delay: 0.55 }}
              className="mt-6 text-sm text-white"
            >
              30-days free trial · No credit card required
            </motion.p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
